import { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';

interface ChallengeDisplayProps {
  challengeId: string;
  variant: 'mole' | 'balance' | 'pixelart' | 'explore';
  config: any;
  socket: Socket | null;
  targetGrid?: number[][];
  initialCollectiveGrid?: number[][];
  initialAverageAccuracy?: number;
}


const DISPLAY_PALETTE = [
  '#1f2937', // 0 = vuoto/sfondo
  '#ef4444', // 1 = rosso
  '#3b82f6', // 2 = blu
  '#22c55e', // 3 = verde
  '#eab308', // 4 = giallo
  '#a855f7', // 5 = viola
  '#f97316', // 6 = arancione
  '#ec4899', // 7 = rosa
  '#06b6d4', // 8 = ciano
];

interface FallingBlock { id: number; side: 'white' | 'black'; col: number }

const MAX_BLOCKS = 100;
// La cima visibile della pila nel viewBox (y=0 è il bordo superiore)
// I blocchi si impilano da y=188 verso l'alto, ma il viewBox taglia a y=0
// Quindi la cima visibile è circa al 5% del contenitore
const PILE_TOP_PCT = 5;

function ChallengeDisplay({ challengeId, variant, config, socket, targetGrid, initialCollectiveGrid, initialAverageAccuracy }: ChallengeDisplayProps) {
  const [score, setScore] = useState(0);
  const [targetScore] = useState(config.targetScore || 50);
  const [remainingSeconds, setRemainingSeconds] = useState(config.durationSeconds || 30);
  const [progressPercent, setProgressPercent] = useState(0);
  const [whiteClicks, setWhiteClicks] = useState(0);
  const [blackClicks, setBlackClicks] = useState(0);
  const [collectiveGrid, setCollectiveGrid] = useState<number[][]>(initialCollectiveGrid || []);
  const [averageAccuracy, setAverageAccuracy] = useState(initialAverageAccuracy || 0);
  const [participantCount, setParticipantCount] = useState(0);

  // Blocchi cadenti per la bilancia (overflow oltre MAX_BLOCKS)
  const [fallingBlocks, setFallingBlocks] = useState<FallingBlock[]>([]);
  const fallingIdRef = useRef(0);
  const whiteClicksRef = useRef(0);
  const blackClicksRef = useRef(0);

  useEffect(() => {
    if (!socket) return;

    const spawnFalling = (side: 'white' | 'black') => {
      const block: FallingBlock = {
        id: ++fallingIdRef.current,
        side,
        col: Math.floor(Math.random() * 4),
      };
      setFallingBlocks(prev => [...prev, block]);
      setTimeout(() => {
        setFallingBlocks(prev => prev.filter(b => b.id !== block.id));
      }, 900);
    };

    const handleTick = (data: any) => {
      if (data.challengeId !== challengeId) return;
      setScore(data.score);
      setRemainingSeconds(data.remainingSeconds);
      setProgressPercent(data.progressPercent);
      if (data.collectiveGrid) setCollectiveGrid(data.collectiveGrid);
      if (data.averageAccuracy !== undefined) setAverageAccuracy(data.averageAccuracy);
      if (data.participantCount !== undefined) setParticipantCount(data.participantCount);
      if (data.whiteClicks !== undefined) {
        whiteClicksRef.current = data.whiteClicks;
        setWhiteClicks(data.whiteClicks);
      }
      if (data.blackClicks !== undefined) {
        blackClicksRef.current = data.blackClicks;
        setBlackClicks(data.blackClicks);
      }
    };

    const handleProgress = (data: any) => {
      if (data.challengeId !== challengeId) return;

      const newW = data.whiteClicks;
      const newB = data.blackClicks;
      const prevW = whiteClicksRef.current;
      const prevB = blackClicksRef.current;

      // Blocchi cadenti per overflow bilancia
      if (newW !== undefined && newW > MAX_BLOCKS && newW > prevW) {
        const count = Math.min(newW - Math.max(prevW, MAX_BLOCKS), 3);
        for (let i = 0; i < count; i++) spawnFalling('white');
      }
      if (newB !== undefined && newB > MAX_BLOCKS && newB > prevB) {
        const count = Math.min(newB - Math.max(prevB, MAX_BLOCKS), 3);
        for (let i = 0; i < count; i++) spawnFalling('black');
      }

      if (newW !== undefined) { whiteClicksRef.current = newW; setWhiteClicks(newW); }
      if (newB !== undefined) { blackClicksRef.current = newB; setBlackClicks(newB); }
      if (data.score !== undefined) setScore(data.score);
      if (data.progressPercent !== undefined) setProgressPercent(data.progressPercent);
      if (data.collectiveGrid) setCollectiveGrid(data.collectiveGrid);
      if (data.averageAccuracy !== undefined) setAverageAccuracy(data.averageAccuracy);
      if (data.participantCount !== undefined) setParticipantCount(data.participantCount);
    };

    socket.on('challenge:tick', handleTick);
    socket.on('challenge:progress', handleProgress);

    return () => {
      socket.off('challenge:tick', handleTick);
      socket.off('challenge:progress', handleProgress);
    };
  }, [socket, challengeId]);

  const balanceMargin = config.balanceMargin || 10;
  const totalBalanceClicks = whiteClicks + blackClicks;
  const balanceRatio = totalBalanceClicks > 0 ? (whiteClicks - blackClicks) / Math.max(totalBalanceClicks, 1) : 0;
  // Clamp tilt angle to -30..+30 degrees
  const tiltAngle = Math.max(-30, Math.min(30, -balanceRatio * 45));

  return (
    <div className="h-screen w-screen display-dark-bg flex flex-col items-center justify-between overflow-hidden p-8">
      {/* Countdown */}
      <div className="text-center">
        {variant !== 'explore' && (
          <div className={`text-8xl font-bold tabular-nums ${
            remainingSeconds <= 5 ? 'text-red-500 animate-pulse' : 'text-parchment-100 font-heading'
          }`}>
            {remainingSeconds}
          </div>
        )}
        <div className="text-parchment-400 font-heading text-xl mt-2">
          {variant === 'explore' ? 'ESPLORA' : variant === 'mole' ? 'PRENDI LA TALPA' : variant === 'balance' ? 'BILANCIA' : 'PIXEL ART'}
        </div>
      </div>

      {/* Centro: Contenuto variante-specifico */}
      <div className="flex-1 flex items-center justify-center w-full">
        {variant === 'pixelart' ? (
          // Pixelart: target + collettiva affiancate
          <div className="flex items-center justify-center gap-12 w-full px-8">
            {/* Target grid */}
            <div className="text-center">
              <div className="text-parchment-400 font-heading text-xl mb-3 font-semibold">OBIETTIVO</div>
              <div
                className="grid gap-[2px] bg-gray-700 p-[2px] rounded-lg"
                style={{
                  gridTemplateColumns: 'repeat(10, 1fr)',
                  width: 'min(35vw, 400px)',
                  height: 'min(35vw, 400px)',
                }}
              >
                {targetGrid?.map((row, r) =>
                  row.map((cell, c) => (
                    <div
                      key={`t-${r}-${c}`}
                      style={{
                        backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937',
                      }}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Info al centro */}
            <div className="text-center" style={{ minWidth: '280px' }}>
              <div className="text-[6rem] font-black text-parchment-100 font-heading leading-none tabular-nums mb-2">
                {Math.round(averageAccuracy)}%
              </div>
              <div className="text-parchment-400 font-body text-2xl">{participantCount} partecipanti</div>
            </div>

            {/* Collective grid */}
            <div className="text-center">
              <div className="text-parchment-400 font-heading text-xl mb-3 font-semibold">RISULTATO</div>
              <div
                className="grid gap-[2px] bg-gray-700 p-[2px] rounded-lg"
                style={{
                  gridTemplateColumns: 'repeat(10, 1fr)',
                  width: 'min(35vw, 400px)',
                  height: 'min(35vw, 400px)',
                }}
              >
                {collectiveGrid.length > 0 ? collectiveGrid.map((row, r) =>
                  row.map((cell, c) => (
                    <div
                      key={`c-${r}-${c}`}
                      style={{
                        backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937',
                      }}
                    />
                  ))
                ) : Array.from({ length: 100 }).map((_, i) => (
                  <div key={`e-${i}`} style={{ backgroundColor: '#1f2937' }} />
                ))}
              </div>
            </div>
          </div>
        ) : variant === 'mole' || variant === 'explore' ? (
          // Mole/Explore: percentuale grande al centro
          <div className="text-center">
            <div className="text-[12rem] font-black text-parchment-100 font-heading leading-none tabular-nums">
              {progressPercent}%
            </div>
            {variant === 'explore' && (
              <div className="text-parchment-400 font-heading text-3xl mt-4">
                {score} / {targetScore} oggetti
              </div>
            )}
          </div>
        ) : (
          // Balance: bilancia SVG
          <div className="flex flex-col items-center w-full max-w-3xl">
            {/* Contatori */}
            <div className="flex justify-between w-full mb-8 px-16">
              <div className="text-center">
                <div className="text-6xl font-bold text-red-400 tabular-nums">{whiteClicks}</div>
                <div className="text-red-400/60 text-2xl mt-1">Rosso</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-parchment-400 font-heading tabular-nums">{score}/{targetScore}</div>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-green-400 tabular-nums">{blackClicks}</div>
                <div className="text-green-400/60 text-2xl mt-1">Verde</div>
              </div>
            </div>

            {/* Bilancia SVG + blocchi cadenti */}
            <div className="relative w-full max-w-2xl">
              <svg viewBox="0 0 600 220" className="w-full">
                {/* Base / Pivot */}
                <polygon points="280,200 320,200 300,160" fill="#666" />
                <circle cx="300" cy="155" r="8" fill="#888" />

                {/* Braccio - ruota attorno al pivot */}
                <g transform={`rotate(${tiltAngle}, 300, 155)`}>
                  {/* Barra orizzontale */}
                  <rect x="80" y="150" width="440" height="10" rx="5" fill="#aaa" />

                  {/* Piatto sinistro (rosso) */}
                  <rect x="80" y="152" width="4" height="40" fill="#888" />
                  <rect x="60" y="192" width="44" height="4" rx="2" fill="#888" />
                  {/* Blocchetti rossi impilati */}
                  {Array.from({ length: Math.min(whiteClicks, 100) }).map((_, i) => (
                    <rect
                      key={`w-${i}`}
                      x={64 + (i % 4) * 10}
                      y={188 - Math.floor(i / 4) * 10}
                      width="8"
                      height="8"
                      rx="1"
                      fill="#ef4444"
                      stroke="#b91c1c"
                      strokeWidth="0.5"
                    />
                  ))}

                  {/* Piatto destro (verde) */}
                  <rect x="516" y="152" width="4" height="40" fill="#888" />
                  <rect x="496" y="192" width="44" height="4" rx="2" fill="#888" />
                  {/* Blocchetti verdi impilati */}
                  {Array.from({ length: Math.min(blackClicks, 100) }).map((_, i) => (
                    <rect
                      key={`b-${i}`}
                      x={500 + (i % 4) * 10}
                      y={188 - Math.floor(i / 4) * 10}
                      width="8"
                      height="8"
                      rx="1"
                      fill="#4ade80"
                      stroke="#16a34a"
                      strokeWidth="0.5"
                    />
                  ))}
                </g>

                {/* Label equilibrio */}
                {Math.abs(whiteClicks - blackClicks) <= balanceMargin ? (
                  <text x="300" y="24" textAnchor="middle" fill="#7dd3fc" fontSize="22" fontWeight="bold">
                    In Equilibrio
                  </text>
                ) : (
                  <text x="300" y="24" textAnchor="middle" fill="#facc15" fontSize="22" fontWeight="bold"
                    style={{ animation: 'balanceBlink 0.6s ease-in-out infinite' }}
                  >
                    Attenzione!
                  </text>
                )}
              </svg>

              {/* Blocchi cadenti HTML (overflow oltre MAX_BLOCKS) */}
              {fallingBlocks.map((block) => {
                // x% relativa al viewBox: piatto rosso ~10-16%, piatto verde ~83-89%
                const xPct = block.side === 'white'
                  ? ((64 + block.col * 10) / 600) * 100
                  : ((500 + block.col * 10) / 600) * 100;
                const bg = block.side === 'white' ? '#ef4444' : '#4ade80';
                const border = block.side === 'white' ? '#b91c1c' : '#16a34a';
                const driftDir = block.side === 'white' ? -1 : 1;
                return (
                  <div
                    key={`fall-${block.id}`}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${xPct}%`,
                      top: `${PILE_TOP_PCT}%`,
                      width: '1.4%',
                      paddingBottom: '1.4%',
                      backgroundColor: bg,
                      border: `1px solid ${border}`,
                      borderRadius: 2,
                      animation: `blockFall 0.9s ease-in forwards`,
                      '--drift': `${driftDir * 50}px`,
                    } as React.CSSProperties}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Progress bar in basso */}
      <div className="w-full max-w-4xl">
        <div className="w-full h-8 bg-parchment-500/30 rounded-full overflow-hidden border-2 border-parchment-500/30">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              variant === 'pixelart'
                ? 'bg-gradient-to-r from-orange-500 to-amber-400'
                : variant === 'balance'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-500'
                  : variant === 'explore'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-400'
                    : 'bg-gradient-to-r from-yellow-500 to-green-500'
            }`}
            style={{ width: `${variant === 'pixelart' ? Math.min(100, Math.round(averageAccuracy)) : progressPercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-parchment-400 text-xl">
          <span>0%</span>
          <span>{progressPercent}%</span>
          <span>100%</span>
        </div>
      </div>

      {variant === 'balance' && (
        <style>{`
          @keyframes blockFall {
            0% { transform: translate(0, 0) scale(1); opacity: 1; }
            20% { transform: translate(calc(var(--drift) * 0.2), -20px) scale(1); opacity: 1; }
            100% { transform: translate(var(--drift), 120px) scale(0.6); opacity: 0; }
          }
          @keyframes balanceBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      )}
    </div>
  );
}

export default ChallengeDisplay;
